import { useEffect, useId, useRef, useState } from 'react';
import type { DraftRevision, Proposal } from '../api/types';
import { Button } from '../components/Button';
import type { DraftDiffView } from '../app/router';
import { RevisionDiffPanel } from '../pages/RevisionDiffPanel';
import { summaryText } from './ProposalBar';

export interface ProposalDiffDialogProps {
  workspaceId: string;
  proposal: Proposal;
  onClose(): void;
}

/**
 * 変更案の版を、S2 の差分パネルが読める版の形にする。親は base（staging を作ったときの head）なので、
 * パネルは getRevisionDiff(base, { to: staging }) を呼ぶ（C32）。表示に使うのは id と parentId だけ。
 */
export function proposalRevision(proposal: Proposal): DraftRevision {
  return {
    id: proposal.revisionId,
    appId: proposal.appId,
    parentId: proposal.baseRevisionId,
    author: 'agent',
    status: 'proposed',
    summary: '変更案',
    stage: proposal.summary?.stage?.to ?? 0,
    fileCount: proposal.summary?.files ?? 0,
    // staging（変更案）は公開しないので常に null。
    publishedVersion: null,
    createdAt: proposal.createdAt,
  };
}

/** 変更案の差分（ファイル / 要素 / 出典）。反映・見送りはバーで行い、ここは見るだけ。 */
export function ProposalDiffDialog({ workspaceId, proposal, onClose }: ProposalDiffDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const [view, setView] = useState<DraftDiffView>('file');

  // StrictMode で effect が 2 回走っても開き直さないよう open を見る（ConfirmDialog と同じ）。
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog || dialog.open) return;
    dialog.showModal();
  }, []);

  const close = () => {
    const dialog = dialogRef.current;
    if (dialog?.open) dialog.close();
    else onClose();
  };

  return (
    <dialog ref={dialogRef} className="dialog studio-diff-dialog" aria-labelledby={titleId} onClose={onClose}>
      <div className="dialog-head">
        <h2 id={titleId} className="dialog-title">
          変更案の差分
        </h2>
      </div>
      <div className="dialog-body">
        <p className="hint">{`${summaryText(proposal.summary)} · 反映するまで下書きは変わりません`}</p>
        <RevisionDiffPanel
          workspaceId={workspaceId}
          appId={proposal.appId}
          revision={proposalRevision(proposal)}
          view={view}
          onSelectView={setView}
        />
        <p className="hint">反映と見送りに取り消しはありません。戻すときはリビジョン履歴から。</p>
      </div>
      <div className="dialog-actions">
        <Button onClick={close}>閉じる</Button>
      </div>
    </dialog>
  );
}
